import {
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  SpartaEmployeeLabor,
  WorkOrderSummary,
  ForemanJobDetail,
} from '@ups/xplat/api/dto';
import moment from 'moment';

export function buildWorkOrderSummaries(
  job: ForemanJobDetail | null | undefined
): WorkOrderSummary[] {
  if (!job) {
    return [];
  }

  return (job.workOrders ?? [])
    .map((wo) => {
      const phaseId = wo.foremanPhaseID;
      const phaseName = wo.phaseName || wo.foremanPhaseID;

      const workOrderEmployees = job.employees.filter((emp) => {
        return emp.labors?.some(
          (labor) => labor.foremanPhaseID === phaseId && labor.bActive
        );
      });

      const straightTime = workOrderEmployees.reduce((total, emp) => {
        return (
          total +
          (emp.labors
            ?.filter(
              (labor) =>
                (labor.foremanEarnCode === 'ST' ||
                  labor.foremanEarnCode === 'HW') &&
                labor.foremanPhaseID === phaseId &&
                labor.bActive
            )
            .reduce((sum, labor) => sum + (labor.hours || 0), 0) || 0)
        );
      }, 0);

      const overtimeHours = workOrderEmployees.reduce((total, emp) => {
        return (
          total +
          (emp.labors
            ?.filter(
              (labor) =>
                labor.foremanEarnCode === 'OT' &&
                labor.foremanPhaseID === phaseId &&
                labor.bActive
            )
            .reduce((sum, labor) => sum + (labor.hours || 0), 0) || 0)
        );
      }, 0);

      const premiumHours = workOrderEmployees.reduce((total, emp) => {
        return (
          total +
          (emp.labors
            ?.filter(
              (labor) =>
                labor.foremanEarnCode === 'PT' &&
                labor.foremanPhaseID === phaseId &&
                labor.bActive
            )
            .reduce((sum, labor) => sum + (labor.hours || 0), 0) || 0)
        );
      }, 0);

      const totalEmployeeHours = workOrderEmployees.reduce((total, emp) => {
        return (
          total +
          (emp.labors
            ?.filter(
              (labor) =>
                labor.foremanPhaseID === phaseId &&
                labor.bActive &&
                (labor.foremanEarnCode === 'ST' ||
                  labor.foremanEarnCode === 'HW' ||
                  labor.foremanEarnCode === 'OT' ||
                  labor.foremanEarnCode === 'PT')
            )
            .reduce((sum, labor) => sum + (labor.hours || 0), 0) || 0)
        );
      }, 0);

      const equipmentHours = (job.equipment ?? []).reduce((total, eq) => {
        return (
          total +
          (eq.usages
            ?.filter(
              (usage) =>
                usage.foremanPhaseID === phaseId &&
                usage.bActive &&
                Number(usage.amount) > 0
            )
            .reduce((sum, usage) => sum + (Number(usage.amount) || 0), 0) || 0)
        );
      }, 0);

      const totalHours = totalEmployeeHours + equipmentHours;

      if (totalHours <= 0) {
        return null;
      }

      const workOrderSpartaLabors = workOrderEmployees.reduce(
        (allLabors, emp) => {
          if (emp.spartaLabors && emp.spartaLabors.length > 0) {
            const normalizedPhaseName = (phaseName || '').trim().toLowerCase();
            const filteredSpartaLabors = emp.spartaLabors.filter(
              (spartaLabor) => {
                const spartaPhase = (spartaLabor.phase || '')
                  .trim()
                  .toLowerCase();
                if (!normalizedPhaseName || !spartaPhase) {
                  return true;
                }
                return spartaPhase === normalizedPhaseName;
              }
            );
            allLabors.push(...filteredSpartaLabors);
          }
          return allLabors;
        },
        [] as SpartaEmployeeLabor[]
      );

      return {
        workOrderNumber: phaseName,
        phaseName,
        straightTime: Math.round(straightTime * 100) / 100,
        overtimeHours: Math.round(overtimeHours * 100) / 100,
        premiumHours: Math.round(premiumHours * 100) / 100,
        totalHours: Math.round(totalEmployeeHours * 100) / 100,
        equipmentHours: Math.round(equipmentHours * 100) / 100,
        spartaLabors: workOrderSpartaLabors,
      } as WorkOrderSummary;
    })
    .filter((summary): summary is WorkOrderSummary => summary !== null);
}

export function calculateEmployeeTotalHours(params: {
  employee: ForemanEmployeeDispatch;
  date: Date;
  shift: number;
  selectedForemanCrewID: string | null;
}) {
  const { employee, date, shift, selectedForemanCrewID } = params;

  const labors = (employee.labors || []).filter(
    (l) =>
      moment(l.dateWorked).format('MM/DD/YYYY') ===
        moment(date).format('MM/DD/YYYY') &&
      (!shift || shift === l.shift) &&
      l.foremanJobCrewID === selectedForemanCrewID
  );

  const earnCodes = ['ST', 'OT', 'PT'];

  employee.totalHoursWorked = labors
    .filter((l) => !!l.bActive && earnCodes.includes(l.foremanEarnCode))
    .reduce((acc, n) => acc + Number(n.hours || 0), 0);
}

export function calculateEquipmentTotalHours(params: {
  equipment: ForemanEquipmentDispatch;
  date: Date;
  shift: number;
  selectedForemanCrewID: string | null;
}) {
  const { equipment, date, shift, selectedForemanCrewID } = params;

  equipment.totalHoursUsed = (equipment.usages || [])
    .filter(
      (l) =>
        moment(l.dateUsed).format('MM/DD/YYYY') ===
          moment(date).format('MM/DD/YYYY') &&
        (!shift || l.shift === shift) &&
        l.foremanJobCrewID === selectedForemanCrewID &&
        !!l.bActive &&
        !l.notUsed
    )
    .reduce((acc, n) => {
      const qty = Number(n.quantity || 1);
      const amount = Number(n.amount || 0);
      return acc + qty * amount;
    }, 0);
}
